import { normalizeFr } from './normalize-fr';

/**
 * Listes de mots français du score SEO. Une seule source, partagée avec les
 * prompts du moteur (les tics interdits au rédacteur sont exactement ceux que
 * le score sanctionne). Comparaisons sans casse ni accents (`normalizeFr`).
 */

/**
 * Mots vides (stopwords) : ignorés dans le matching de mots-clés et la diversité lexicale.
 * Écrits avec leurs accents (lisible) mais REPLIÉS à la construction : tous les appelants
 * interrogent le Set avec des tokens déjà passés par `normalizeFr`, donc une entrée accentuée
 * ne matchait jamais — « été », « très », « même », « après »… comptaient comme mots pleins.
 */
const RAW_STOPWORDS: readonly string[] = [
  'a',
  'à',
  'au',
  'aux',
  'avec',
  'ce',
  'ces',
  'cet',
  'cette',
  'dans',
  'de',
  'des',
  'du',
  'd',
  'l',
  'elle',
  'en',
  'et',
  'eux',
  'il',
  'ils',
  'je',
  'j',
  'la',
  'le',
  'les',
  'leur',
  'leurs',
  'lui',
  'ma',
  'mais',
  'me',
  'même',
  'mes',
  'moi',
  'mon',
  'ne',
  'n',
  'ni',
  'nos',
  'notre',
  'nous',
  'on',
  'ou',
  'où',
  'par',
  'pas',
  'pour',
  'qu',
  'que',
  'qui',
  'sa',
  'se',
  's',
  'ses',
  'son',
  'sur',
  'ta',
  'te',
  't',
  'tes',
  'toi',
  'ton',
  'tu',
  'un',
  'une',
  'vos',
  'votre',
  'vous',
  'y',
  'c',
  'ça',
  'cela',
  'ceci',
  'celui',
  'celle',
  'ceux',
  'celles',
  'est',
  'sont',
  'été',
  'être',
  'était',
  'étaient',
  'sera',
  'seront',
  'suis',
  'es',
  'sommes',
  'êtes',
  'ai',
  'as',
  'avons',
  'avez',
  'ont',
  'avoir',
  'avait',
  'avaient',
  'aura',
  'auront',
  'fait',
  'faire',
  'plus',
  'moins',
  'très',
  'peu',
  'trop',
  'aussi',
  'donc',
  'car',
  'si',
  'comme',
  'sans',
  'sous',
  'vers',
  'chez',
  'entre',
  'ici',
  'là',
  'lors',
  'puis',
  'tout',
  'tous',
  'toute',
  'toutes',
  'autre',
  'autres',
  'quel',
  'quelle',
  'quels',
  'quelles',
  'dont',
  'afin',
  'ainsi',
  'alors',
  'après',
  'avant',
  'bien',
  'déjà',
  'encore',
  'jamais',
  'toujours',
  'souvent',
  'peut',
  'peuvent',
  'pouvez',
  'doit',
  'doivent',
  'devez',
  'vont',
  'va',
  'aller',
  'the',
  'of',
  'and',
];

export const FR_STOPWORDS: ReadonlySet<string> = new Set(RAW_STOPWORDS.map(normalizeFr));

/** Connecteurs logiques (phrase = « avec transition » si elle en contient un). */
export const FR_TRANSITION_WORDS: readonly string[] = [
  'ainsi',
  'alors',
  'aussi',
  'autrement dit',
  'avant tout',
  'bref',
  'cependant',
  'certes',
  "c'est pourquoi",
  'concrètement',
  'd abord',
  "d'abord",
  'de plus',
  'de même',
  'dès lors',
  'donc',
  'du coup',
  'en bref',
  'en clair',
  'en effet',
  'en fait',
  'en outre',
  'en pratique',
  'en résumé',
  'en revanche',
  'enfin',
  'ensuite',
  'par ailleurs',
  'par conséquent',
  'par exemple',
  'pourtant',
  'premièrement',
  'deuxièmement',
  'puis',
  'résultat',
  'si bien que',
  'surtout',
  'toutefois',
  'à ce stade',
  'à l inverse',
  "à l'inverse",
  'à noter',
  'au contraire',
  'au final',
  'autre point',
  'dans ce cas',
  'dans la pratique',
  'en clair',
  'en particulier',
  'notamment',
  'par contre',
  'plus précisément',
  'pour autant',
  'pour finir',
  'pour commencer',
  'quant à',
  'sinon',
  'de ce fait',
  'or',
  'néanmoins',
  'malgré tout',
  'en somme',
  'finalement',
  'également',
  'd ailleurs',
  "d'ailleurs",
  'ceci dit',
  'cela dit',
  'dans un premier temps',
  'dans un second temps',
  'ensuite',
  'à ce titre',
  'à cet égard',
];

/**
 * Tics de rédaction « IA » : formules creuses, méta-discours, superlatifs
 * automatiques. Comparés sur le texte normalisé (sans accents, minuscules).
 */
export const FR_AI_PHRASES: readonly string[] = [
  'dans cet article',
  'dans ce guide',
  'dans ce billet',
  'nous allons voir',
  'nous allons explorer',
  'nous allons decouvrir',
  'plongeons',
  'plongez',
  'decouvrons ensemble',
  'explorons',
  'il est important de noter',
  'il est essentiel de',
  'il est crucial de',
  'il convient de noter',
  'il est a noter',
  'notez que',
  'en conclusion',
  'pour conclure',
  'en resume',
  'n hesitez pas a',
  'n hesitez pas',
  'que vous soyez',
  'dans un monde ou',
  'dans un monde en constante evolution',
  'a l ere du',
  'a l ere numerique',
  'de nos jours',
  'aujourd hui plus que jamais',
  'veritable',
  'incontournable',
  'optimal',
  'optimale',
  'au coeur de',
  'un large eventail',
  'une panoplie',
  'une pleiade',
  'sans plus attendre',
  'sans plus tarder',
  'cerise sur le gateau',
  'le saint graal',
  'un atout majeur',
  'un levier puissant',
  'booster',
  'revolutionner',
  'transformer votre',
  'sublimer',
  'sublimez',
  'en un mot',
  'en definitive',
  'somme toute',
  'il ne fait aucun doute',
  'force est de constater',
  'il va sans dire',
  'comme son nom l indique',
  'a la fois',
  'non seulement',
  'ne se contente pas',
  'bien plus qu un',
  'bien plus qu une',
  'plus qu un simple',
  'plus qu une simple',
  'que ce soit',
  'quel que soit votre',
  'un voyage',
  'une aventure',
  'une experience unique',
  'une experience inoubliable',
  'faire la difference',
  'passer au niveau superieur',
  'a ne pas negliger',
  'a ne pas manquer',
  'vous l aurez compris',
  'en effet il',
  'alors pourquoi attendre',
  'prets a',
  'pret a franchir le pas',
  'franchir le pas',
  'sauter le pas',
  // Relecture du 2026-09-06 : l'ouverture « l'enjeu n'est pas seulement… » et ses variantes.
  'l enjeu n est pas',
  'l enjeu est de taille',
  'ne se limite pas a',
  'va bien au dela',
  'n est pas qu une question de',
  'pas seulement une question de',
  'plusieurs criteres entrent en jeu',
  'plusieurs facteurs entrent en jeu',
  'plusieurs elements entrent en jeu',
];

/**
 * Méta-discours d'introduction (formes normalisées) : l'article annonce ce qu'il va faire au
 * lieu de commencer. Relecture du 2026-09-06 : « nous détaillons ici les éléments… », « l'objectif
 * est que vous puissiez… ». Mesuré sur les paragraphes AVANT le premier intertitre uniquement.
 */
export const FR_META_INTRO_MARKERS: readonly string[] = [
  'nous detaillons',
  'nous vous detaillons',
  'nous expliquons',
  'nous vous expliquons',
  'nous allons',
  'on va voir',
  'vous allez decouvrir',
  'vous decouvrirez',
  'vous trouverez ici',
  'vous trouverez dans',
  'ce guide',
  'cet article',
  'ce billet',
  'cette page',
  'l objectif est',
  'l objectif de cet',
  'l objectif de ce',
  'pour vous permettre de',
  'pour vous aider a y voir',
  'y voir plus clair',
  'faisons le point',
  'on fait le point',
  'nous faisons le point',
  'tour d horizon',
  'passons en revue',
  'voici ce qu il faut savoir',
  'tout ce qu il faut savoir',
  'ce qu il faut savoir',
  'decryptage',
  'zoom sur',
  'les elements qui',
  'les criteres qui',
  'les facteurs qui',
];

/**
 * Transitions énumératives adverbiales (formes normalisées, en début de phrase) : « D'abord… »,
 * « Ensuite… », « Enfin… ». Deux ou plus dans une même section = la liste du brief déroulée.
 */
export const FR_ENUMERATIVE_ADVERBS: readonly string[] = [
  'd abord',
  'tout d abord',
  'pour commencer',
  'dans un premier temps',
  'premierement',
  'deuxiemement',
  'troisiemement',
  'quatriemement',
  'ensuite',
  'dans un second temps',
  'dans un deuxieme temps',
  'dans un troisieme temps',
  'enfin',
  'pour finir',
  'en dernier lieu',
];

/**
 * Ordinaux à article (« Le premier… », « La seconde… ») : ce n'est une transition que suivis
 * d'une ponctuation, d'un verbe d'état ou d'un nom d'inventaire (`FR_ENUMERATIVE_FOLLOWERS`) ;
 * « le premier rendez-vous » est un simple groupe nominal (calibration du 2026-09-06).
 */
export const FR_ENUMERATIVE_ORDINALS: readonly string[] = [
  'le premier',
  'la premiere',
  'le second',
  'la seconde',
  'le deuxieme',
  'la deuxieme',
  'le troisieme',
  'la troisieme',
  'le quatrieme',
  'la quatrieme',
  'le cinquieme',
  'la cinquieme',
  'le dernier',
  'la derniere',
];

export const FR_ENUMERATIVE_FOLLOWERS: ReadonlySet<string> = new Set([
  'est',
  'sera',
  'reste',
  'concerne',
  'consiste',
  'porte',
  'repose',
  'tient',
  'vient',
  'touche',
  'releve',
  'point',
  'critere',
  'facteur',
  'element',
  'motif',
  'levier',
  'besoin',
  'raison',
  'regle',
  'etape',
  'indicateur',
  'exigence',
  'priorite',
  'avantage',
  'conseil',
  'reflexe',
  'piege',
  'erreur',
  'objectif',
  'axe',
  'geste',
  'cas',
  'aspect',
  'option',
  'solution',
  'question',
  'sujet',
  'argument',
  'poste',
  'chose',
  'categorie',
  'type',
  'niveau',
  'signe',
  'cause',
  'frein',
  'obstacle',
  'pilier',
  'principe',
  'mesure',
  'action',
  'parametre',
  'variable',
  'volet',
  'famille',
  'grande',
  'grand',
]);

/** Anglicismes courants à éviter dans un article grand public en français. */
export const FR_ANGLICISMS: readonly string[] = [
  'impacter',
  'impacte',
  'challenger',
  'challenge',
  'booster',
  'boost',
  'process',
  'feedback',
  'feedbacks',
  'game changer',
  'focus sur',
  'focus',
  'workflow',
  'deadline',
  'brainstorming',
  'checker',
  'switcher',
  'customiser',
  'customise',
  'dispatcher',
  'forwarder',
  'meeting',
  'call',
  'asap',
  'best practices',
  'bullet points',
  'call to action',
  'digital',
  'digitaliser',
  'digitalisation',
  'disruptif',
  'disrupter',
  'start up',
  'startup',
  'scalable',
  'scaler',
  'mindset',
  'lifestyle',
  'wording',
  'insight',
  'insights',
  'kick off',
  'one shot',
  'top niveau',
  'must have',
  'nice to have',
  'win win',
  'gagnant gagnant',
  'in fine',
  'benchmark',
  'brief',
  'briefer',
  'deal',
  'dealer',
  'sponsoriser',
  'trigger',
  'update',
  'upgrade',
  'upgrader',
  'hack',
  'hacks',
  'tips',
  'tip',
  'astuces et tips',
  'hashtag',
  'trendy',
  'has been',
  'made in',
  'shopping',
  'pitch',
  'pitcher',
];

/** Ancres de liens faibles (non descriptives). */
export const FR_WEAK_ANCHORS: readonly string[] = [
  'ici',
  'cliquez ici',
  'cliquer ici',
  'clique ici',
  'lire la suite',
  'en savoir plus',
  'plus d infos',
  'plus d informations',
  'ce lien',
  'lien',
  'cette page',
  'page',
  'voir',
  'voir plus',
  'suite',
  'la',
  'là',
  'source',
  'article',
  'cet article',
  'site',
  'ce site',
  'notre site',
  'clic',
  'clique',
  'consulter',
];

/**
 * Marqueurs d'appel à l'action à l'impératif (verbe seul ou verbe + objet).
 * Compte les phrases qui en contiennent : trop = article publicitaire, zéro = pas de suite.
 * Précédé de « vous », le verbe est à l'indicatif et décrit une situation
 * (« vous prenez rendez-vous pour votre coupe ») : la règle ne le compte pas.
 */
export const FR_CTA_MARKERS: readonly string[] = [
  'reservez',
  'prenez rendez vous',
  'contactez',
  'appelez',
  'demandez un devis',
  'essayez',
  'inscrivez vous',
  'inscrivez',
  'creez votre compte',
  'commencez',
  'demarrez',
  'decouvrez notre',
  'decouvrez nos',
  'profitez',
  'commandez',
  'telechargez',
  'rejoignez',
  'abonnez vous',
  'planifiez',
  'programmez',
  'reservez des maintenant',
  'reservez votre',
  'prenez contact',
  'venez',
  'passez nous voir',
  'testez',
];

/**
 * Marqueurs d'appel à l'action à l'infinitif. Seuls, ils sont ambigus (« le stress de prendre
 * rendez-vous ») : ils ne comptent que précédés, dans la même phrase, d'une invitation
 * (`FR_CTA_INVITATION_CUES`), comme « vous pouvez réserver en ligne ».
 */
export const FR_CTA_INFINITIVE_MARKERS: readonly string[] = [
  'reserver',
  'prendre rendez vous',
  'contacter',
  'demander un devis',
  'essayer gratuitement',
];

/**
 * Tournures qui font d'un infinitif placé plus loin dans la phrase un appel à l'action
 * (« vous pouvez vérifier le tarif et réserver »). « pour » n'en fait partie que collé à
 * l'infinitif (« pour réserver, … ») : voir la règle `CTA_COUNT`.
 */
export const FR_CTA_INVITATION_CUES: readonly string[] = [
  'vous pouvez',
  'vous pourrez',
  'n hesitez pas a',
  'il suffit de',
  'pensez a',
];

/**
 * Marqueurs d'un raisonnement de modèle déversé dans l'article (formes
 * normalisées, sans accents ni apostrophes). Vu au bench du 2026-09-04 :
 * cinq articles Kimi commençaient par « L'utilisateur me demande de rédiger… »
 * et le juge LLM leur a donné 92 à 100. Un seul marqueur suffit à bloquer — d'où des
 * formes qui ne peuvent PAS apparaître dans un article écrit à la première personne
 * (« je dois respecter », « vérifions les » sont du français ordinaire : exclus).
 */
export const FR_REASONING_LEAK_MARKERS: readonly string[] = [
  'l utilisateur me demande',
  'l utilisateur demande',
  'on me demande de rediger',
  'je dois rediger',
  'je dois produire',
  'je vais rediger',
  'je vais structurer',
  'analysons les contraintes',
  'analysons le brief',
  'le brief dit',
  'le brief impose',
  'le brief exige',
  'le brief demande',
  'selon le brief',
  'la consigne dit',
  'les consignes disent',
  'verifions maintenant',
  'comptons les mots',
  'calculons la densite',
  'plan de redaction',
  'format de sortie',
  'mot cle principal a placer',
  'faits autorises',
  'en tant que modele de langage',
  'en tant qu ia',
  'en tant qu intelligence artificielle',
  'je suis une ia',
  'voici l article',
  'voici votre article',
  'voici le texte',
  'j espere que cet article',
  'n hesitez pas a me demander',
];

/**
 * Fuites de source ou de consigne : le rédacteur cite la provenance interne
 * d'un fait (« d'après notre grille tarifaire ») au lieu de l'énoncer.
 */
export const FR_SOURCE_LEAK_MARKERS: readonly string[] = [
  'd apres notre grille tarifaire',
  'selon notre grille tarifaire',
  'd apres la grille tarifaire',
  'selon les reperes metier',
  'd apres les reperes metier',
  'selon les faits autorises',
  'd apres les faits autorises',
  'selon les faits fournis',
  'd apres le brief',
  'source : offre',
  'source : brief',
  'source : grille',
  'source : reperes',
  'selon l offre propelsia',
  'd apres l offre propelsia',
];

/** Auxiliaires du passif (formes normalisées). */
export const FR_PASSIVE_AUX: ReadonlySet<string> = new Set([
  'est',
  'sont',
  'etait',
  'etaient',
  'sera',
  'seront',
  'fut',
  'furent',
  'ete',
  'etre',
  'serait',
  'seraient',
  'soit',
  'soient',
]);

/** Abréviations courantes : le point qui les suit ne termine pas une phrase. */
export const FR_ABBREVIATIONS: readonly string[] = [
  'm',
  'mm',
  'mme',
  'mlle',
  'dr',
  'pr',
  'me',
  'etc',
  'ex',
  'cf',
  'p',
  'pp',
  'n',
  'no',
  'st',
  'ste',
  'av',
  'bd',
  'tel',
  'tél',
  'env',
  'min',
  'max',
  'vs',
  'art',
  'fig',
  'chap',
  'vol',
  'trad',
  'ed',
  'éd',
  'nb',
  'ps',
  'hab',
];

/** Mots interrogatifs en tête de phrase (questions rhétoriques). */
export const FR_QUESTION_STARTERS: readonly string[] = [
  'pourquoi',
  'comment',
  'quand',
  'ou',
  'que',
  'quel',
  'quelle',
  'quels',
  'quelles',
  'qui',
  'combien',
  'est ce que',
  'faut il',
  'peut on',
  'devez vous',
  'avez vous',
  'etes vous',
  'vous etes vous',
  'et si',
  'alors',
];
